import { Button, Card, CardBody, CardHeader, Chip, Divider, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, Textarea, Switch } from "@nextui-org/react";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useMemo, useState, useCallback } from "react";
import ModalAlert from "../../../../components/elements/ModalAlert";
import useMaterial from "../../../../hooks/master-data/useMaterial";
import useSWR from "swr";
import { getFetcher, putFetcher } from "../../../../services/api";
import toast from "react-hot-toast";
import { getErrorMessage } from "../../../../services/errorMessage";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import ControlledInput from "../../../../components/elements/ControlledInput";

export default function IndexPage() {
  /*
   * Halaman master adalah SATU-SATUNYA pemanggil yang meminta termasukNonaktif.
   * Tanpa parameter itu endpoint hanya mengirim baris aktif - yang memang
   * diinginkan untuk modal pemilihan bahan pada pelaporan, tapi di sini
   * pengelola justru perlu melihat baris nonaktif agar bisa mengaktifkannya lagi.
   */
  const { data, isLoading, mutate } = useSWR(`/api/data-master/bahan-b3?returnAll=true&termasukNonaktif=true`, getFetcher);

  const [idUbahStatus, setIdUbahStatus] = useState(null);
  const {
    isEdit,
    onClickEdit,
    onClickDelete,
    onSubmitDelete,
    onCloseForm,
    onSubmitForm,
    modalForm: { onOpenModalForm, isOpenModalForm, onOpenChangeModalForm },
    modalAlert: { isOpenModalAlert, onOpenChangeModalAlert },
    hookForm: {
      register,
      handleSubmit,
      control,
      formState: { errors, isSubmitting, dirtyFields },
    },
  } = useMaterial({ mutate });

  // Fetch related dropdown data
  const { data: kategoriOptions, isLoading: isKategoriLoading } = useSWR(`/api/data-master/kategori-b3`, getFetcher);
  const { data: saranaOptions, isLoading: isSaranaLoading } = useSWR(`/api/data-master/sarana-angkut-b3`, getFetcher);
  const { data: klasifikasiOptions, isLoading: isKlasifikasiLoading } = useSWR(`/api/data-master/klasifikasi-b3`, getFetcher);
  const { data: karateristikOptions, isLoading: isKarateristikLoading } = useSWR(`/api/data-master/karateristik-b3`, getFetcher);

  function HSCode(hsCode) {
    if (!hsCode) return null; // Jika null atau undefined, langsung kembalikan null

    if (Array.isArray(hsCode)) {
      // Jika hsCode adalah array, bersihkan setiap elemen

      return hsCode.map((code) => (typeof code === "string" ? code.replace(/[^0-9]/g, "") : code));
    }

    // Jika hsCode adalah string, bersihkan dan kembalikan
    return typeof hsCode === "string" ? hsCode.replace(/[^0-9]/g, "") : null;
  }

  /*
   * Menonaktifkan, bukan menghapus.
   *
   * Baris master B3 tidak bisa begitu saja dihapus: relasinya memakai
   * onDelete RESTRICT dan sebagian baris sudah ditunjuk laporan yang
   * difinalisasi serta disetujui. Penanda ini menyembunyikannya dari pilihan
   * bahan pada pelaporan BARU tanpa menyentuh satu angka pun di riwayat.
   */
  const handleToggleActive = useCallback(
    async (row, aktif) => {
      setIdUbahStatus(row?.id);
      try {
        await putFetcher("/api/data-master/bahan-b3", row.id, { isActive: aktif });
        toast.success(aktif ? `${row?.namaBahanKimia} diaktifkan kembali` : `${row?.namaBahanKimia} dinonaktifkan dari pilihan pelaporan`);
        await mutate();
      } catch (error) {
        toast.error(getErrorMessage(error, "Gagal mengubah status bahan B3"));
      } finally {
        setIdUbahStatus(null);
      }
    },
    [mutate],
  );

  const columns = useMemo(
    () => [
      { field: "casNumber", headerName: "Cas Number/Nomor Kimia" },
      { field: "namaBahanKimia", headerName: "Nama Bahan Kimia" },
      { field: "namaDagang", headerName: "Nama Dagang" },
      {
        field: "hsCode",
        headerName: "HS Code",
        renderCell: (params) => <span>{HSCode(params?.row?.hsCode)?.join(", ") ?? "-"}</span>,
      },
      {
        field: "tipeBahan",
        headerName: "Tipe Bahan",
        renderCell: (params) => {
          switch (params.row.tipeBahan) {
            case "DILARANG":
              return (
                <Chip color="danger" variant="flat" size="sm">
                  DILARANG
                </Chip>
              ); // Render a specific component or value for 'value1'
            case "TERBATAS DIPERGUNAKAN":
              return (
                <Chip color="warning" variant="flat" size="sm">
                  TERBATAS DIPERGUNAKAN
                </Chip>
              ); // Render a specific component or value for 'value2' // Render a
            default:
              return (
                <Chip color="success" variant="flat" size="sm">
                  {params.row.tipeBahan}
                </Chip>
              ); // Default rendering for any other values
          }
        },
        sortable: false,
        filterable: false,
      },
      {
        field: "isActive",
        headerName: "Status",
        renderCell: (params) => {
          const aktif = params.row.isActive !== false;
          return (
            <div className="flex items-center gap-2">
              <Switch
                size="sm"
                isSelected={aktif}
                isDisabled={idUbahStatus === params.row.id}
                onValueChange={(nilai) => handleToggleActive(params.row, nilai)}
                aria-label={`Status ${params.row.namaBahanKimia}`}
              />
              <span className={`text-xs ${aktif ? "" : "text-default-500"}`}>{aktif ? "Aktif" : "Nonaktif"}</span>
            </div>
          );
        },
        sortable: false,
        filterable: false,
      },
      {
        field: "action",
        headerName: "Aksi",
        renderCell: (params) => (
          <div className="flex items-center gap-1">
            <Button size="sm" onPress={() => onClickEdit(params.row)} color="warning" isIconOnly>
              <PencilSquareIcon className="size-4" />
            </Button>
            <Button size="sm" onPress={() => onClickDelete(params.row.id)} color="danger" isIconOnly>
              <TrashIcon className="size-4" />
            </Button>
          </div>
        ),
        sortable: false,
        filterable: false,
      },
    ],
    [onClickEdit, onClickDelete, handleToggleActive, idUbahStatus]
  );

  const [currentPage, setCurrentPage] = useState(0); // 0-based untuk MUI DataGrid
  const [searchQuery, setSearchQuery] = useState("");
  
  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0); // Reset ke halaman pertama saat search berubah
  }, []);

  const filteredB3 = useMemo(() => {
    // If data is not available, return an empty array to prevent errors.
    if (!data?.data) {
      return [];
    }

    const kunci = searchQuery.trim().toLowerCase();
    if (!kunci) return data.data;

    // Nomor CAS dicocokkan juga tanpa tanda hubung, karena pengguna lazim
    // mengetik "50000" untuk "50-00-0". Nama bahan dijaga dengan ?? "" sebab
    // ada baris master yang namaBahanKimia-nya kosong - sebelumnya baris
    // seperti itu membuat pencarian melempar TypeError.
    const tanpaStrip = (nilai) => (nilai ?? "").toString().toLowerCase().replace(/-/g, "");
    const kunciTanpaStrip = tanpaStrip(kunci);

    return data.data.filter((b3) => {
      const nama = (b3.namaBahanKimia ?? "").toLowerCase();
      const cas = (b3.casNumber ?? "").toLowerCase();
      return nama.includes(kunci) || cas.includes(kunci) || tanpaStrip(cas).includes(kunciTanpaStrip);
    });
  }, [data?.data, searchQuery]); // The filter will re-run only when data or the search query changes.


  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">Daftar Bahan B3</p>
        </CardHeader>
        <Divider />
        <CardBody className="w-full h-[550px] p-5">
          <div className="mb-5" style={{ display: 'flex', alignItems: 'right', gap: '15px' }}>
            <Button onPress={onOpenModalForm} size="md" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
              Tambah
            </Button>
            <SearchField className="ml-5" value={searchQuery} onChange={handleSearchChange} placeholder="Cari nama B3 atau nomor CAS" />
          </div>
          <ClientDataGrid
            rows={filteredB3}
            rowCount={data?.total || 0}
            isLoading={isLoading}
            columns={columns}
            autoResize={false}
            initialState={{
              sorting: {
                sortModel: [{ field: "casNumber", sort: "asc" }],
              },
            }}
          />
        </CardBody>
      </Card>

      <ModalAlert isOpen={isOpenModalAlert} onOpenChange={onOpenChangeModalAlert} onSubmit={onSubmitDelete} icon="danger" />
      <Modal size="2xl" scrollBehavior="inside" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{isEdit ? "Ubah" : "Tambah"} Bahan B3</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmit(onSubmitForm)}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ControlledInput control={control} label="CAS Number" name="casNumber" isRequired />
                    <ControlledInput control={control} label="Nama Bahan Kimia" name="namaBahanKimia" isRequired />
                    <ControlledInput control={control} label="Nama Dagang" name="namaDagang" isRequired />
                    <ControlledInput control={control} label="HS Code" name="hsCode" />
                    <ControlledInput control={control} label="Nama IUPAC" name="namaIupac" />
                    <ControlledInput control={control} label="Nama Trivial" name="namaTrivial" />
                    <ControlledInput control={control} label="Nama Trivial" name="namaTrivial" />
                    <ControlledInput control={control} label="Rumus Molekul" name="rumusMolekul" />
                    <ControlledInput control={control} label="Rumus Struktur" name="rumusStruktur" />
                    <ControlledInput control={control} label="Berat Molekul (Mr)" name="beratMr" />
                    <ControlledInput control={control} label="Sifat Fisik" name="sifatFisik" />
                    <ControlledInput control={control} label="Titik Didih" name="titikDidih" />
                    <ControlledInput control={control} label="Titik Lebur" name="titikLebur" />
                    <ControlledInput control={control} label="Kelarutan" name="kelarutan" />
                    <ControlledInput control={control} label="Densitas" name="densitas" />
                    <ControlledInput control={control} label="Tekanan Uap" name="tekananUap" />
                    <ControlledInput control={control} label="Kegunaan" name="kegunaan" />
                    <ControlledInput control={control} label="Dampak Kesehatan" name="dampakKesehatan" />
                    <ControlledInput control={control} label="Penanganan Resiko" name="penangananResiko" />
                    <ControlledInput control={control} label="APD" name="apd" />
                    <div className="col-span-2">
                      <Switch {...register("statusStockholm")} color={errors.statusStockholm ? "danger" : "primary"} isInvalid={errors.statusStockholm} errorMessage={errors.statusStockholm && errors.statusStockholm.message}>
                        Status Stockholm
                      </Switch>
                    </div>
                    <ControlledInput control={control} label="URL" name="url" />
                    <Select
                      {...register("kategoriBahanId")}
                      label="Kategori Bahan B3"
                      labelPlacement="outside"
                      placeholder="..."
                      variant="faded"
                      color={errors.kategoriBahanId ? "danger" : "default"}
                      isInvalid={errors.kategoriBahanId}
                      isLoading={isKategoriLoading}
                      errorMessage={errors.kategoriBahanId && errors.kategoriBahanId.message}
                      isRequired
                    >
                      {kategoriOptions?.map((item) => (
                        <SelectItem key={item.id}>{item.namaKategori}</SelectItem>
                      ))}
                    </Select>
                    <Select
                      {...register("saranaAngkutBahanB3Id")}
                      label="Kategori Sarana Angkut"
                      labelPlacement="outside"
                      placeholder="..."
                      variant="faded"
                      color={errors.saranaAngkutBahanB3Id ? "danger" : "default"}
                      isInvalid={errors.saranaAngkutBahanB3Id}
                      isLoading={isSaranaLoading}
                      errorMessage={errors.saranaAngkutBahanB3Id && errors.saranaAngkutBahanB3Id.message}
                      isRequired
                    >
                      {saranaOptions?.map((item) => (
                        <SelectItem key={item.id}>{item.namaSaranaAngkut}</SelectItem>
                      ))}
                    </Select>
                    <Select
                      {...register("klasifikasiBahanB3Id")}
                      label="Klasifikasi Bahan B3"
                      labelPlacement="outside"
                      placeholder="..."
                      variant="faded"
                      color={errors.klasifikasiBahanB3Id ? "danger" : "default"}
                      isInvalid={errors.klasifikasiBahanB3Id}
                      isLoading={isKlasifikasiLoading}
                      errorMessage={errors.klasifikasiBahanB3Id && errors.klasifikasiBahanB3Id.message}
                      isRequired
                    >
                      {klasifikasiOptions?.map((item) => (
                        <SelectItem key={item.id}>{item.namaKlasifikasi}</SelectItem>
                      ))}
                    </Select>
                    <Select
                      {...register("karateristikBahanB3Id")}
                      label="Karakteristik Bahan B3"
                      labelPlacement="outside"
                      placeholder="..."
                      variant="faded"
                      color={errors.karateristikBahanB3Id ? "danger" : "default"}
                      isInvalid={errors.karateristikBahanB3Id}
                      isLoading={isKarateristikLoading}
                      errorMessage={errors.karateristikBahanB3Id && errors.karateristikBahanB3Id.message}
                      isRequired
                    >
                      {karateristikOptions?.map((item) => (
                        <SelectItem key={item.id}>{item.namaKarateristik}</SelectItem>
                      ))}
                    </Select>
                    <div className="col-span-2">
                      <Textarea
                        {...register("keterangan")}
                        label="Keterangan"
                        variant="faded"
                        labelPlacement="outside"
                        color={errors.keterangan ? "danger" : "default"}
                        isInvalid={errors.keterangan}
                        errorMessage={errors.keterangan && errors.keterangan.message}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-1 mt-5">
                    <Button isLoading={isSubmitting} isDisabled={isSubmitting || (isEdit && Object.keys(dirtyFields).length === 0)} type="submit" color="primary">
                      {isEdit ? "Simpan" : "Tambah"}
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}

function SearchField({ value, onChange, placeholder }) {
  return (  
    <div className="flex w-full  items-center">
      <div className="w-full sm:w-1/4">
        <input
          type="text"
          id="search"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}
